import { useMemo, useRef, useState } from "react";
import { Box, ButtonBase, Stack, Typography, useMediaQuery } from "@mui/material";
import { visuallyHidden } from "../../../common/visuallyHidden";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import type { BracketMatchResponse } from "@bw/shared/types/generated";
import { BracketMatchCard, isLiveMatch } from "./BracketMatchCard";
import { PillChip } from "../../../common/components/PillChip";

/**
 * A single-column bracket: one round at a time behind a round switch
 * (My matches · R32 · R16 · QF · SF · Final), never a sideways tree.
 *
 * - Rounds come from the server's `round_number`; labels count back from the
 *   last round (Final, SF, QF, then R<n>).
 * - A live dot marks every round with a match in play.
 * - It opens on My matches when the viewer has an entry in the draw, otherwise
 *   on the current round: the first round with a match in play, else the first
 *   round still to be played, else the final.
 * - Swiping left/right moves between rounds.
 * - Draws with three rounds or fewer (and `stacked` callers such as Results)
 *   list every round instead of switching.
 */

type RoundKey = number | "mine";

interface Round {
  number: number;
  label: string;
  longLabel: string;
  matches: BracketMatchResponse[];
  live: boolean;
}

export function roundLabels(roundNumber: number, totalRounds: number) {
  const fromEnd = totalRounds - roundNumber;
  if (fromEnd === 0) return { label: "Final", longLabel: "Final" };
  if (fromEnd === 1) return { label: "SF", longLabel: "Semifinal" };
  if (fromEnd === 2) return { label: "QF", longLabel: "Quarterfinal" };
  const size = 2 ** (fromEnd + 1);
  return { label: `R${size}`, longLabel: `Round of ${size}` };
}

export function groupRounds(
  matches: readonly BracketMatchResponse[],
  /** The draw's last round number when `matches` is a subset (e.g. completed). */
  totalRounds?: number,
): Round[] {
  const byRound = new Map<number, BracketMatchResponse[]>();
  for (const match of matches) {
    const list = byRound.get(match.round_number) ?? [];
    list.push(match);
    byRound.set(match.round_number, list);
  }
  const numbers = [...byRound.keys()].sort((a, b) => a - b);
  const total =
    totalRounds ?? (numbers.length ? numbers[numbers.length - 1] : 0);
  return numbers.map((number) => {
    const roundMatches = byRound.get(number) ?? [];
    return {
      number,
      ...roundLabels(number, total),
      matches: roundMatches,
      live: roundMatches.some(isLiveMatch),
    };
  });
}

function currentRound(rounds: Round[]): number | undefined {
  return (
    rounds.find((round) => round.live)?.number ??
    rounds.find((round) =>
      round.matches.some((match) => match.status === "pending" || match.status === "scheduled"),
    )?.number ??
    rounds[rounds.length - 1]?.number
  );
}

function isMine(match: BracketMatchResponse, myEntryIds: ReadonlySet<string>) {
  return (
    (match.entry1_id != null && myEntryIds.has(match.entry1_id)) ||
    (match.entry2_id != null && myEntryIds.has(match.entry2_id))
  );
}

export function DrawRounds({
  matches,
  myEntryIds,
  stacked = false,
  totalRounds,
  newestFirst = false,
  empty = "No matches yet",
}: {
  matches: readonly BracketMatchResponse[];
  myEntryIds: ReadonlySet<string>;
  stacked?: boolean;
  /** Label rounds against the whole draw when `matches` is a subset. */
  totalRounds?: number;
  /** Stacked lists put the latest round first (Results). */
  newestFirst?: boolean;
  empty?: string;
}) {
  const rounds = useMemo(() => {
    const grouped = groupRounds(matches, totalRounds);
    return newestFirst ? grouped.reverse() : grouped;
  }, [matches, totalRounds, newestFirst]);
  const myMatches = useMemo(
    () => matches.filter((match) => isMine(match, myEntryIds)),
    [matches, myEntryIds],
  );
  const entered = myMatches.length > 0;
  const [picked, setPicked] = useState<RoundKey | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const coarsePointer = useMediaQuery("(pointer: coarse)");

  if (matches.length === 0) {
    return <Typography color="text.secondary">{empty}</Typography>;
  }

  const card = (match: BracketMatchResponse) => (
    <BracketMatchCard
      key={match.match_id}
      match={match}
      mine={isMine(match, myEntryIds)}
    />
  );

  if (stacked || rounds.length <= 3) {
    return (
      <Stack spacing={2}>
        {rounds.map((round) => (
          <Box component="section" key={round.number} aria-label={round.longLabel}>
            {rounds.length > 1 ? (
              <Typography
                component="h3"
                sx={{ fontSize: "0.875rem", fontWeight: 700, mb: 1 }}
              >
                {round.longLabel}
              </Typography>
            ) : null}
            <Stack spacing={1}>{round.matches.map(card)}</Stack>
          </Box>
        ))}
      </Stack>
    );
  }

  const keys: RoundKey[] = [...(entered ? (["mine"] as const) : []), ...rounds.map((round) => round.number)];
  const fallback: RoundKey = entered ? "mine" : (currentRound(rounds) ?? rounds[0].number);
  const active: RoundKey = picked !== null && keys.includes(picked) ? picked : fallback;
  const activeIndex = keys.indexOf(active);
  const shown = active === "mine" ? myMatches : (rounds.find((round) => round.number === active)?.matches ?? []);
  const nextKey = keys[activeIndex + 1];
  const nextRound = active !== "mine" && typeof nextKey === "number" ? rounds.find((round) => round.number === nextKey) : undefined;
  const activeName =
    active === "mine"
      ? "My matches"
      : (rounds.find((round) => round.number === active)?.longLabel ?? "");
  const move = (step: number) => {
    const next = keys[activeIndex + step];
    if (next !== undefined) setPicked(next);
  };

  return (
    <Stack spacing={1.25}>
      <Stack
        role="group"
        aria-label="Round"
        direction="row"
        spacing={0.75}
        sx={{ overflowX: "auto", scrollbarWidth: "none", pb: 0.25 }}
      >
        {keys.map((key) => {
          const round = key === "mine" ? null : rounds.find((r) => r.number === key);
          return (
            <PillChip
              key={String(key)}
              selected={key === active}
              onClick={() => setPicked(key)}
              ariaLabel={key === "mine" ? "My matches" : `${round?.longLabel}${round?.live ? ", in play" : ""}`}
              live={Boolean(round?.live)}
            >
              {key === "mine" ? "My matches" : round?.label}
            </PillChip>
          );
        })}
      </Stack>
      {/* A short announcement of the round change, not the whole list. */}
      <Typography aria-live="polite" sx={visuallyHidden}>
        {`${activeName}, ${shown.length} ${shown.length === 1 ? "match" : "matches"}`}
      </Typography>
      <Stack
        spacing={1}
        onTouchStart={(event) => {
          const touch = event.touches[0];
          touchStart.current = { x: touch.clientX, y: touch.clientY };
        }}
        onTouchEnd={(event) => {
          const start = touchStart.current;
          touchStart.current = null;
          if (!start) return;
          const touch = event.changedTouches[0];
          const dx = touch.clientX - start.x;
          const dy = touch.clientY - start.y;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) move(dx < 0 ? 1 : -1);
        }}
      >
        {shown.map(card)}
      </Stack>
      {nextRound ? (
        <ButtonBase
          onClick={() => move(1)}
          sx={{ alignSelf: "center", gap: 0.25, color: "text.secondary", fontSize: "0.8125rem", py: 0.5, px: 1, borderRadius: 2 }}
        >
          {coarsePointer ? "Swipe for " : "Next: "}
          {nextRound.longLabel}
          <ChevronRightRounded sx={{ fontSize: 18 }} aria-hidden />
        </ButtonBase>
      ) : null}
    </Stack>
  );
}
